import { useState, useEffect } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { anonymizeName } from "@/utils/anonymization";

interface Profile {
  id: string;
  first_name: string;
  age: number;
  location: string;
  gender: string;
}

interface ProfileSelectorProps {
  onSelect: (profile: Profile | null) => void;
  selectedProfile: Profile | null;
  anonymized?: boolean;
}

export function ProfileSelector({ onSelect, selectedProfile, anonymized = false }: ProfileSelectorProps) {
  const [open, setOpen] = useState(false);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, age, location, gender")
        .eq("is_active", true)
        .eq("profile_completed", true)
        .order("first_name");

      if (error) throw error;
      setProfiles(data || []);
    } catch (error) {
      console.error("profile_selector.fetch_failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {selectedProfile
            ? `${anonymized ? anonymizeName(selectedProfile.first_name) : selectedProfile.first_name}, ${selectedProfile.age} (${selectedProfile.location})`
            : "Select profile..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0">
        <Command>
          <CommandInput placeholder="Search profiles..." />
          <CommandList>
            <CommandEmpty>
              {loading ? "Loading..." : "No profiles found."}
            </CommandEmpty>
            <CommandGroup>
              {profiles.map((profile) => {
                const displayName = anonymized ? anonymizeName(profile.first_name) : profile.first_name;
                return (
                  <CommandItem
                    key={profile.id}
                    value={`${displayName} ${profile.age} ${profile.location}`}
                    onSelect={() => {
                      onSelect(profile.id === selectedProfile?.id ? null : profile);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedProfile?.id === profile.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex-1">
                      <div className="font-medium">
                        {displayName}, {profile.age}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {profile.gender} • {profile.location}
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
